import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout, Card } from '../components';
import { Breadcrumb } from '../components/Breadcrumb';
import { contractApi } from '../services/api';

interface PendingContract {
  projectId: string;
  projectNumber: number;
  contractId: string;
  contractNumber: number;
  description: string;
  contractAmount: string;
  draftStatus: string;
  companyNumber: number;
  companyName: string;
  customerName: string;
  customerNumber: number;
}

export function AdminContractReview() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [contracts, setContracts] = useState<PendingContract[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    contractApi.getAdminPendingReview()
      .then(data => setContracts(data))
      .catch(err => console.error('Failed to load pending contracts:', err))
      .finally(() => setIsLoading(false));
  }, []);

  const filtered = search
    ? contracts.filter(c => {
        const s = search.toLowerCase();
        return (
          c.description.toLowerCase().includes(s) ||
          c.companyName.toLowerCase().includes(s) ||
          c.customerName.toLowerCase().includes(s) ||
          String(c.projectNumber).includes(s) ||
          String(c.contractNumber).includes(s) ||
          String(c.companyNumber).includes(s)
        );
      })
    : contracts;

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'draft_rejected': return 'danger';
      case 'draft_approved': return 'success';
      case 'draft_sent': return 'info';
      case 'signed_by_company': return 'primary';
      case 'signed_by_person': return 'primary';
      case 'signed_by_both': return 'success';
      default: return 'secondary';
    }
  };

  const formatAmount = (amount: string) =>
    new Intl.NumberFormat('sv-SE').format(parseFloat(amount?.replace(/[^0-9.-]/g, '') || '0'));

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">{t('common.loading')}</span>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="page-header mb-4">
        <div className="page-header-left d-flex align-items-center">
          <div className="page-header-title">
            <h5 className="fw-bold mb-0">{t('adminContractReview.title')}</h5>
          </div>
          <Breadcrumb items={[
            { label: t('nav.home'), path: '/admin/home' },
            { label: t('admin.nav.reports') },
            { label: t('adminContractReview.title') }
          ]} />
        </div>
        <div className="page-header-right ms-auto d-none d-sm-block">
          <p className="fw-bold text-muted mb-0">{t('adminContractReview.description')}</p>
        </div>
      </div>

      <Card>
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div className="d-flex align-items-center gap-3">
            <input
              type="text"
              className="form-control form-control-sm"
              placeholder={t('adminContractReview.search')}
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{ width: '280px' }}
            />
          </div>
          <span className="text-muted small">{t('adminContractReview.total')}: {filtered.length}</span>
        </div>

        {filtered.length === 0 ? (
          <div className="text-center py-5 text-muted">
            <i className="feather-check-circle fs-1 d-block mb-3"></i>
            <p className="mb-0">{t('adminContractReview.noActivity')}</p>
          </div>
        ) : (
          <div className="d-flex flex-column gap-3">
            {filtered.map(pc => (
              <div key={pc.contractId} className="border rounded p-3">
                <div className="d-flex flex-column flex-md-row justify-content-between align-items-start gap-2">
                  <div className="flex-grow-1">
                    <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
                      <span className="badge bg-light text-dark border">
                        <i className="feather-briefcase me-1"></i>
                        {pc.companyNumber} — {pc.companyName}
                      </span>
                      <span className="badge bg-light text-dark border">
                        <i className="feather-user me-1"></i>
                        #{pc.customerNumber} — {pc.customerName}
                      </span>
                    </div>
                    <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
                      <span className="badge bg-light text-dark border">
                        <i className="feather-folder me-1"></i>
                        {t('adminContractReview.project')} #{pc.projectNumber}
                      </span>
                      <span className="badge bg-light text-dark border">
                        <i className="feather-file-text me-1"></i>
                        {t('adminContractReview.contract')} #{pc.contractNumber}
                      </span>
                      <span className={`badge bg-${getStatusColor(pc.draftStatus)}`}>
                        {t(`contracts.draftStatus.${pc.draftStatus}`)}
                      </span>
                    </div>
                    <div className="fw-semibold mb-1">{pc.description}</div>
                    <div className="text-muted small">
                      <i className="feather-credit-card me-1"></i>
                      {t('adminContractReview.amount')}: {formatAmount(pc.contractAmount)} kr
                    </div>
                  </div>
                  <button
                    className="btn btn-sm btn-primary flex-shrink-0"
                    onClick={() => navigate(`/admin/projects/${pc.projectId}?tab=contract&contractNumber=${pc.contractNumber}`)}
                  >
                    <i className="feather-eye me-1"></i>
                    {t('adminContractReview.reviewButton')}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </DashboardLayout>
  );
}
